function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from "react";
import cache from "memory-cache";
import NextApp from "next/app";
import { compose, fetchClient } from "@lululemon/mwa-utils";
import { withFederationPrerender } from "../federation";
import publicConfig from "../common/public-config";
// HOCs
import withDeviceCharacterization from "../hoc/with-device-char";
import withGlobalContext from "../hoc/with-global-context";
import sseHOC from "../hoc/with-sse";
import withAnalytics from "../hoc/with-analytics";
import withSigninModal from "../hoc/with-signin-modal";
import withSessionProvider from "../hoc/with-session-provider";
const LayoutPassthrough = ({
  children
}) => children;
if (typeof document !== "undefined") {
  window.cachedData = cache;
} else {
  global.cachedData = cache;
}
const MwapApp = ({
  Component,
  pageProps,
  layoutProps
}) => {
  // Use the Layout defined at the page level for just use a passthrough
  const AppLayout = Component.Layout || LayoutPassthrough;
  return /*#__PURE__*/React.createElement(AppLayout, layoutProps, /*#__PURE__*/React.createElement(Component, pageProps));
};

/**
 * @param {import("next/app").AppContext} appContext
 */
MwapApp.getInitialProps = async appContext => {
  var _appContext$ctx, _appContext$Component, _appContext$Component2, _appContext$Component3;
  appContext.ctx.fetch = fetchClient((_appContext$ctx = appContext.ctx) === null || _appContext$ctx === void 0 ? void 0 : _appContext$ctx.req);
  const prepareInitialPropsContext = appContext.Component.prepareInitialPropsContext;
  const postProcessInitialProps = appContext.Component.postProcessInitialProps;
  const layoutGetInitialProps = (_appContext$Component = appContext.Component.Layout) === null || _appContext$Component === void 0 ? void 0 : _appContext$Component.getInitialProps;
  const layoutPrepareInitialPropsContext = (_appContext$Component2 = appContext.Component.Layout) === null || _appContext$Component2 === void 0 ? void 0 : _appContext$Component2.prepareInitialPropsContext;
  const layoutPostProcessInitialProps = (_appContext$Component3 = appContext.Component.Layout) === null || _appContext$Component3 === void 0 ? void 0 : _appContext$Component3.postProcessInitialProps;
  await (layoutPrepareInitialPropsContext === null || layoutPrepareInitialPropsContext === void 0 ? void 0 : layoutPrepareInitialPropsContext(appContext));
  await (prepareInitialPropsContext === null || prepareInitialPropsContext === void 0 ? void 0 : prepareInitialPropsContext(appContext.ctx));
  let [initialProps, layoutProps] = await Promise.all([NextApp.getInitialProps(appContext), // Treat the layout component getInitialProps the same as
  // implementing _app.js getInitialProps
  layoutGetInitialProps === null || layoutGetInitialProps === void 0 ? void 0 : layoutGetInitialProps(appContext)]);

  // This introduces a place for HOC's to tap into to do pre-pass render
  // data collection for libraries such as apollo-client as well as modify
  // getInitialProps results introducing a place for PEPS to do personalization.
  // eslint-disable-next-line sonarjs/no-collapsible-if
  if (!process.browser) {
    if (layoutPostProcessInitialProps || appContext.Component.postProcessInitialProps) {
      const {
        renderToString
      } = await import("react-dom/server");

      // TODO: move to react-ssr-prepass to avoid slow serialization of react render
      renderToString( /*#__PURE__*/React.createElement(appContext.AppTree, _extends({}, initialProps, {
        layoutProps
      })));
      [layoutProps, initialProps.pageProps] = await Promise.all([layoutPostProcessInitialProps ? layoutPostProcessInitialProps(layoutProps) : layoutProps, postProcessInitialProps ? postProcessInitialProps(initialProps.pageProps) : initialProps.pageProps]);
    }
  }
  return _extends({}, initialProps, {
    layoutProps
  });
};

// withSSE is evaluated before jest and crashes suite.
// __NEXT_DATA__ is required for withSSE
const emptyCompose = Component => Component;
const withSSE = process.env.NODE_ENV !== "test" ? sseHOC : emptyCompose;
export default compose(withAnalytics, withSessionProvider, withSigninModal, withFederationPrerender(publicConfig === null || publicConfig === void 0 ? void 0 : publicConfig.remoteEntries), withDeviceCharacterization, withSSE, withGlobalContext)(MwapApp);